import pino, { Logger } from "pino";
import { v4 as uuidv4 } from "uuid";
// TODO: PINO LOGGER ALWAYS OUTPUTS TO CONSOLE NEED TO SET THIS UP TO OUTPUT TO EITHER DB OR PIPELINE
export const masterLogger = pino({
    level: `${process.env.NEXT_PUBLIC_PINO_LOG_LEVEL ?? "debug"}`, // NOTE IF YOU NEED TO SEE LOGS SET TO DEBUG SHOWS UP IN CHROME CONSOLE. ONCE IN DEV ENVIRONMENT SHOULD ROUTE TO AZURE DB FOR LOGGING
    redact: ["email", "profileName", "password", "address"],
    timestamp: () => `",timestamp":"${new Date(Date.now()).toISOString()}"`,
});

/**
 * Class for a Logger Object that generated a traceId and can create child loggers with that traceId.
 * @param functionName: the name of the function that the logger is created in
 * @param parentLoggerObject: the logger object that corresponds to the parent function.
 * If not provided, the logger object is created with a new traceId with no parent logger
 *
 * Attributes:
 * traceId: automatically generated uuidv4 string that is unique for each logger and its children
 * functionName: the name of the function that the logger is created in
 * parentFunctionName: the name of the function that called the function that the logger is created in
 * logger: the pino logger object that is created with the traceId, functionName, and parentFunctionName
 */
export class LoggerObject {
    traceId: string;
    functionName: string;
    parentFunctionName: string;
    logger: Logger<never>;

    constructor(functionName: string, parentLoggerObject?: LoggerObject) {
        this.functionName = functionName;

        if (!parentLoggerObject) {
            this.traceId = uuidv4();
            this.parentFunctionName = "";
            this.logger = masterLogger.child({
                traceId: this.traceId,
                function: this.functionName,
                parentFunction: this.parentFunctionName,
            });
        } else {
            this.traceId = parentLoggerObject.getTraceId();
            this.parentFunctionName = parentLoggerObject.getFunctionName();
            this.logger = parentLoggerObject.getLogger().child({
                traceId: this.traceId,
                function: this.functionName,
                parentFunction: this.parentFunctionName,
            });
        }
    }

    getFunctionName() {
        return this.functionName;
    }

    getTraceId() {
        return this.traceId;
    }

    setTraceId(traceId: string) {
        this.traceId = traceId;
    }

    getLogger() {
        return this.logger;
    }

    createChildLogger(functionName: string) {
        return new LoggerObject(functionName, this);
    }

    getParentFunctionName() {
        return this.parentFunctionName;
    }

    setParentFunctionName(parentFunctionName: string) {
        this.parentFunctionName = parentFunctionName;
    }
}

export default masterLogger;

export function sanitizeLogMessage(...messages: string[]): string {
    return messages.map((message) => message.replace(/[^\w\s]/gi, "_")).join(" "); // Replace non-alphanumeric characters with an underscore
}
